import React, { useState } from "react";

/**
 * AuthoringNotePanel — the coordinator's direction for authoring (Nov 1).
 *
 * One box on the hub: what every stream should emphasize, in the
 * coordinator's words. Stored now; PE reads it once contract v3.5 ships (its
 * top-level `direction`, never client data, so it can't be cited as a fact).
 * It is never read aloud and never shown to DSPs. Changing it does not
 * re-author anything already made.
 */
export const AUTHORING_COPY = {
  title: "Authoring note (optional)",
  help: "Direction for every stream: what to emphasize, what to avoid. Not read aloud, and not shown to DSPs. Streams start following it after PE's next update; until then it is saved here.",
  placeholder: "e.g. The tone throughout should emphasize her social communication and repetitive and compulsive behaviors, not just how bright and charming she is.",
  saved: "Saved.",
  max: 1000,
};

export default function AuthoringNotePanel({ clientId, initial = "", onSaved }) {
  const [text, setText] = useState(initial || "");
  const [savedText, setSavedText] = useState(initial || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const dirty = text.trim() !== (savedText || "").trim();
  const over = text.length > AUTHORING_COPY.max;

  async function save() {
    setBusy(true); setError(null); setMsg(null);
    try {
      const r = await fetch(`/api/clients/${clientId}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authoring_note: text }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't save that.");
      const v = d.authoring_note || "";
      setText(v); setSavedText(v);
      setMsg(AUTHORING_COPY.saved);
      onSaved?.(v);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={s.wrap}>
      <p style={s.help}>{AUTHORING_COPY.help}</p>
      <textarea style={s.box} rows={4} value={text} aria-label={AUTHORING_COPY.title}
                placeholder={AUTHORING_COPY.placeholder} disabled={busy}
                onChange={e => { setText(e.target.value); setMsg(null); setError(null); }} />
      <div style={s.row}>
        <span style={{ ...s.count, ...(over ? s.over : {}) }}>{text.length} / {AUTHORING_COPY.max}</span>
        <button type="button" style={{ ...s.save, ...(!dirty || over || busy ? s.saveOff : {}) }}
                disabled={!dirty || over || busy} onClick={save}>Save</button>
      </div>
      {msg && <p role="status" style={s.msg}>{msg}</p>}
      {error && <p style={s.error}>{error}</p>}
    </div>
  );
}

const s = {
  wrap:   { display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  help:   { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  box:    { font: "inherit", fontSize: "var(--text-sm)", lineHeight: 1.5, width: "100%", boxSizing: "border-box", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", resize: "vertical" },
  row:    { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)" },
  count:  { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  over:   { color: "var(--color-critical)" },
  save:   { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  saveOff:{ opacity: 0.5, cursor: "default" },
  msg:    { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-accent)" },
  error:  { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-critical)" },
};
